import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Intent } from "@blueprintjs/core";
import { ShowHide } from "components/layout";
import { Breadcrumbs, Tabs } from "components/navigation";
import {
    AlertConfirm,
    NotificationInline,
    NotificationToaster,
} from "components/notifications";
import { PageRestricted } from "pages/errors/page-restricted";
import { GreyFleetCheckVehicle } from "pages/licence-checking/grey-fleet/check/vehicle-check/GreyFleetCheckVehicle";
import { GreyFleetCheckInsurance } from "pages/licence-checking/grey-fleet/check/insurance-check/GreyFleetCheckInsurance";
import { GreyFleetCheckDetail } from "pages/licence-checking/grey-fleet/check/details/GreyFleetCheckDetail";
import { GreyFleetCheckLicence } from "pages/licence-checking/grey-fleet/check/licence-check/GreyFleetCheckLicence";
import { useParams, useNavigate } from "react-router-dom";
import { GreyFleetService, LicenceCheckingService } from "services";
import { Button } from "components/buttons";
import { Tooltip } from "components/tooltip";
import PropTypes from "prop-types";
import axios from "axios";
import { StatusBadge } from "components/status";
import { useSelector } from "react-redux";

export function GreyFleetCheck({ breadcrumbs }) {
    const noAccessCode = 403;
    const disabledTabMessage =
        "Step will become available once the relevant information has been received";
    const invalidTaskStatusKeys = ["AWAITINGAPPROVAL", "NOTSTARTED"];
    const { checkId } = useParams();
    var navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [showUnauthorizedAccess, setShowUnauthorizedAccess] = useState(false);
    const [tabs, setTabs] = useState([]);
    const [currentTab, setCurrentTab] = useState(0);

    const [licenceCheckingOptions, setLicenceCheckingOptions] = useState({});
    const [greyFleetCheck, setGreyFleetCheck] = useState({});
    const [greyFleetSettings, setGreyFleetSettings] = useState({});
    const [greyFleetPolicy, setGreyFleetPolicy] = useState([]);
    const [coverTypes, setCoverTypes] = useState([]);

    const [deletingInProgress, setDeletingInProgress] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const [isRechecking, setIsRechecking] = useState(false);

    const greyFleetCredits = useSelector(
        (state) => state.licenceChecking.greyFleetCredits
    );

    const isEnrolled = useMemo(
        () =>
            greyFleetCheck?.enrolled === true ||
            greyFleetCheck?.statusKey === "REJECTED",
        [greyFleetCheck]
    );

    const canRecheck = useMemo(() => {
        if (
            greyFleetCheck?.statusKey !== "DONE" ||
            !Array.isArray(greyFleetCheck?.failures)
        ) {
            return false;
        }
        const criticalReasons = ["MOTFAILURE", "TAXFAILURE", "VEHICLENOTFOUND"];
        return greyFleetCheck.failures.some((failure) =>
            criticalReasons.includes(failure)
        );
    }, [greyFleetCheck]);

    const recheckDisabledByDate = useMemo(() => {
        if (!greyFleetCheck?.lastRecheckDate) return false;
        const lastRecheckDate = new Date(greyFleetCheck.lastRecheckDate);
        const now = new Date();
        return now.getTime() - lastRecheckDate.getTime() < 24 * 60 * 60 * 1000;
    }, [greyFleetCheck]);

    useEffect(() => {
        if (!checkId) return;
        getCheck();
    }, [checkId]);

    const checkNotifications = useMemo(() => {
        if (!greyFleetCheck) {
            return [];
        }
        let notifications = [];

        if (greyFleetCredits <= 0) {
            notifications.push(
                <NotificationInline
                    text="You have run out of credits, until you have added more credits some data may be out of date and not processed. Please add more credits to resume functionality."
                    intent="warning"
                    show
                />
            );
        }

        if (greyFleetCheck.passed && greyFleetCheck.statusKey === "DONE") {
            notifications.push(
                <NotificationInline
                    key="check-pass"
                    show
                    intent="success"
                    text="This grey fleet check has passed"
                />
            );
        }
        if (greyFleetCheck.statusKey === "INPROGRESS") {
            notifications.push(
                <NotificationInline
                    key="check-in-progress"
                    show
                    intent="primary"
                    text="This grey fleet check is in progress"
                />
            );
        }
        if (
            Array.isArray(greyFleetCheck.failures) &&
            Array.isArray(licenceCheckingOptions.failureReasons)
        ) {
            greyFleetCheck.failures.forEach((failureKey) => {
                const reason = licenceCheckingOptions.failureReasons.find(
                    (r) => r.key === failureKey
                );
                if (reason) {
                    notifications.push(
                        <NotificationInline
                            key={`failure-${failureKey}`}
                            show
                            intent={reason.hardFail ? "danger" : "warning"}
                            text={
                                <span>
                                    <span className="semi-bold">
                                        {reason.name}:
                                    </span>{" "}
                                    <span>{reason.description}</span>
                                </span>
                            }
                        />
                    );
                }
            });
        }
        return notifications;
    }, [greyFleetCheck, licenceCheckingOptions]);

    const getCheck = useCallback(() => {
        setIsLoading(true);
        axios
            .all([
                LicenceCheckingService.getLicenceCheckingOptions(),
                LicenceCheckingService.getGreyFleetCheck(checkId),
                LicenceCheckingService.getGreyFleetSettings(),
                LicenceCheckingService.getGreyFleetPolicy(),
                GreyFleetService.getInsuranceCoverTypes(),
            ])
            .then(
                axios.spread(function (
                    optionsLicence,
                    check,
                    settings,
                    policy,
                    coverTypeOptions
                ) {
                    setLicenceCheckingOptions(optionsLicence);
                    setGreyFleetCheck(check);
                    setGreyFleetSettings(settings);
                    setGreyFleetPolicy(policy);
                    setCoverTypes(coverTypeOptions ?? []);

                    const routeConfigs = [
                        {
                            title: "Insurance Check",
                            key: "insurance",
                            disabled:
                                (invalidTaskStatusKeys.includes(
                                    check.insurance.statusKey
                                ) &&
                                    check.insurance.result === null) ||
                                check.statusKey === "REJECTED",
                            showTooltip:
                                invalidTaskStatusKeys.includes(
                                    check.insurance.statusKey
                                ) &&
                                check.insurance.result === null &&
                                check.statusKey !== "REJECTED",
                            toolTipMessage: disabledTabMessage,
                        },
                        {
                            title: "Vehicle Check",
                            key: "vehicle",
                            disabled:
                                (invalidTaskStatusKeys.includes(
                                    check.mot.statusKey
                                ) &&
                                    invalidTaskStatusKeys.includes(
                                        check.tax.statusKey
                                    ) &&
                                    invalidTaskStatusKeys.includes(
                                        check.asset.statusKey
                                    )) ||
                                check.statusKey === "REJECTED" ||
                                check?.failures?.includes("VEHICLENOTFOUND"),

                            showTooltip:
                                invalidTaskStatusKeys.includes(
                                    check.mot.statusKey
                                ) &&
                                invalidTaskStatusKeys.includes(
                                    check.tax.statusKey
                                ) &&
                                invalidTaskStatusKeys.includes(
                                    check.asset.statusKey
                                ) &&
                                check.statusKey !== "REJECTED",
                            toolTipMessage: disabledTabMessage,
                        },
                        {
                            title: "Licence Check",
                            key: "licence",
                            disabled:
                                (invalidTaskStatusKeys.includes(
                                    check.licence.statusKey
                                ) &&
                                    check.licence.result === null) ||
                                check.statusKey === "REJECTED",
                            showTooltip:
                                invalidTaskStatusKeys.includes(
                                    check.licence.statusKey
                                ) &&
                                check.licence.result === null &&
                                check.statusKey !== "REJECTED",
                            toolTipMessage: disabledTabMessage,
                        },
                    ];

                    setTabs([
                        {
                            title: "Details",
                            key: "Details",
                            selected: true,
                            disabled: false,
                        },
                        ...routeConfigs,
                    ]);
                }),
                (error) => {
                    if (error.response.status === noAccessCode) {
                        setShowUnauthorizedAccess(true);
                    } else {
                        NotificationToaster.show(
                            Intent.DANGER,
                            "Could not retrieve grey fleet check data"
                        );
                    }
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not retrieve grey fleet check data"
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [checkId]);

    const changeCurrentTab = useCallback(
        (index) => {
            if (index === currentTab) return;
            let clonedTabs = tabs.map((t, i) => ({
                ...t,
                selected: index === i,
            }));
            setTabs(clonedTabs);
            setCurrentTab(index);
        },
        [currentTab, tabs]
    );

    const onTabClick = useCallback(
        (index) => {
            if (isLoading) return;
            changeCurrentTab(index);
        },
        [isLoading, changeCurrentTab]
    );

    const onDeleteCheck = useCallback(() => {
        setDeletingInProgress(true);
        LicenceCheckingService.deleteGreyFleetCheck(checkId)
            .then(
                () => {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "Check deleted successfully."
                    );
                    navigate(-1);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Check could not be deleted. Please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Check could not be deleted. Please try again."
                );
            })
            .finally(() => {
                setDeletingInProgress(false);
            });
    }, [checkId, history]);

    function evaluateStatus(
        taskStatus,
        taskStatusKey,
        taskResult,
        failureKeys
    ) {
        let icon = "";
        let text = "";
        let intent;

        if (taskResult === true) {
            icon = "tick-circle";
            intent = Intent.SUCCESS;
        } else if (taskResult === false) {
            icon = "error";
        } else {
            icon = "refresh";
            intent = Intent.PRIMARY;
        }

        // Array of failure keys that relate to this task
        const keysArray = Array.isArray(failureKeys)
            ? failureKeys
            : [failureKeys];

        // Find this first key that matches a failure in the grey fleet check
        const foundFailure = keysArray.find((key) =>
            greyFleetCheck?.failures?.includes(key)
        );

        // Find the failure reason based on the found failure key
        const failureReason = licenceCheckingOptions?.failureReasons?.find(
            (fr) => fr.key === foundFailure
        );

        // Determine the intent based on failure reason or default to danger
        if (failureReason) {
            intent = failureReason?.hardFail ? Intent.DANGER : Intent.WARNING;
        } else if (taskResult === false) {
            intent = Intent.DANGER;
        }

        // Determine the text to show in the badge
        if (taskStatusKey !== "COMPLETED") {
            text =
                taskStatusKey === "AWAITINGRESPONSE"
                    ? "Awaiting Employee"
                    : taskStatus;
        } else if (failureReason) {
            text = failureReason?.name;
        } else if (taskResult === false) {
            text = "Invalid";
        } else if (taskResult === null) {
            text = "Calculating";
        } else {
            text = "Valid";
        }

        return (
            <StatusBadge
                text={text}
                iconName={icon}
                intent={intent}
                showTooltip={
                    taskResult === false && taskStatusKey !== "COMPLETED"
                }
                tooltipContent={failureReason?.name}
                tooltipPosition="auto"
            />
        );
    }

    function onRecheck() {
        setIsRechecking(true);
        LicenceCheckingService.recheckGreyFleetCheck(checkId)
            .then(
                () => {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "Check re-evaluated successfully."
                    );
                    getCheck();
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Check could not be re-evaluated. Please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Check could not be re-evaluated. Please try again."
                );
            })
            .finally(() => {
                setIsRechecking(false);
            });
    }

    return (
        <ShowHide
            evaluator={!showUnauthorizedAccess}
            show={
                <div className="row">
                    <Breadcrumbs items={breadcrumbs} />
                    <h1>Grey Fleet Check</h1>
 
                    <div className="notification-inline-holder">
                        {checkNotifications}
                    </div>

                    <div className="spacer-bottom button-row">
                        <ShowHide
                            evaluator={isEnrolled}
                            show={
                                <Button
                                    id="btn-delete-check"
                                    intent={Intent.DANGER}
                                    onClick={() => setConfirmingDelete(true)}
                                    disabled={deletingInProgress}
                                    text="Delete Check"
                                />
                            }
                        />
                        <ShowHide
                            evaluator={canRecheck}
                            show={
                                recheckDisabledByDate ? (
                                    <Tooltip
                                        content="You can only recheck details once a day."
                                        position="top"
                                    >
                                        <Button
                                            id="btn-recheck"
                                            intent={Intent.PRIMARY}
                                            onClick={onRecheck}
                                            disabled={true}
                                            text="Recheck"
                                        />
                                    </Tooltip>
                                ) : (
                                    <Button
                                        id="btn-recheck"
                                        intent={Intent.PRIMARY}
                                        onClick={onRecheck}
                                        disabled={isLoading || isRechecking}
                                        text="Recheck"
                                    />
                                )
                            }
                        />
                    </div>

                    <div className="spacer-bottom">
                        <Tabs tabs={tabs} onClick={onTabClick} />
                    </div>
                    <ShowHide
                        evaluator={currentTab === 0}
                        show={
                            <GreyFleetCheckDetail
                                isLoading={isLoading}
                                checkData={greyFleetCheck}
                                evaluateStatus={evaluateStatus}
                                refreshCheck={getCheck}
                                licenceCheckingOptions={licenceCheckingOptions}
                                coverTypes={coverTypes}
                            />
                        }
                    />
                    <ShowHide
                        evaluator={currentTab === 1}
                        show={
                            <GreyFleetCheckInsurance
                                isLoading={isLoading}
                                checkData={greyFleetCheck}
                                evaluateStatus={evaluateStatus}
                                reloadCheck={getCheck}
                                coverTypes={coverTypes}
                            />
                        }
                    />
                    <ShowHide
                        evaluator={currentTab === 2}
                        show={
                            <GreyFleetCheckVehicle
                                isLoading={isLoading}
                                checkData={greyFleetCheck}
                                evaluateStatus={evaluateStatus}
                                refreshCheck={getCheck}
                                licenceCheckingOptions={licenceCheckingOptions}
                                policy={greyFleetPolicy}
                                coverTypes={coverTypes}
                            />
                        }
                    />
                    <ShowHide
                        evaluator={currentTab === 3}
                        show={
                            <GreyFleetCheckLicence
                                isLoading={isLoading}
                                checkData={greyFleetCheck}
                                evaluateStatus={evaluateStatus}
                                reloadCheck={getCheck}
                                settings={greyFleetSettings}
                                policy={greyFleetPolicy}
                            />
                        }
                    />

                    <AlertConfirm
                        isOpen={confirmingDelete}
                        onConfirm={onDeleteCheck}
                        onCancel={() => setConfirmingDelete(false)}
                        title="Confirm Delete"
                        confirmButtonText="Delete"
                        confirmButtonIntent={Intent.DANGER}
                    >
                        Are you sure you want to delete this check?
                    </AlertConfirm>
                </div>
            }
            hide={<PageRestricted />}
        />
    );
}

GreyFleetCheck.propTypes = {
    breadcrumbs: PropTypes.array.isRequired,
};
